<template>
  <div class="c-pb" v-if="!loading">
    <div class="row">
      <!-- PAGE ELEMENTS -->
      <div class="col-xs-12 col-md-8">
        <slot name="preFields"></slot>
        <div class="c-pb__container" ref="pbContainer">
          <div class="c-pb__no-elements" v-if="!pageElements.length">
            <slot name="intro">
              <span class="c-pb__no-elements-text">Ready to start?</span>
              <span class="c-pb__no-elements-text"
                >Drag and drop elements from the right</span
              >
              <inline-svg name="page-builder"></inline-svg>
            </slot>
          </div>
          <draggable
            class="c-pb__drag-zone"
            :options="{ group: 'pageElements' }"
            v-model="pageElements"
            @start="onPageElementDragStart"
            @end="onPageElementDragEnd"
          >
            <div
              class="c-pb__row"
              v-for="(element, index) in pageElements"
              :key="index"
            >
              <!-- <pre>
                  {{element}}
              </pre>-->
              <pb-element-bar
                @clickEdit="editElement(index)"
                @clickDelete="removeElement(index)"
                @clickDuplicate="duplicateElement(index)"
              ></pb-element-bar>
              <pb-element :element="elementsForRender[index]"></pb-element>
            </div>
          </draggable>
        </div>
      </div>

      <!-- ELEMENT LIST -->
      <div
        class="col-xs-12 col-md-4 js-sidebar"
        :class="{ 'js-sidebar--isOpen': mobileToggle }"
      >
        <div class="sidebar-holder">
          <div sticky-container :style="sidebarWrapperStyleObject">
            <div
              class="c-pb__sidebar"
              :style="sidebarStyleObject"
              v-sticky="sidebarShouldStick"
              sticky-side="top"
            >
              <slot name="tabs">
                <tabs>
                  <tabs-item
                    :active="activeSbPanel === 'elements'"
                    @click="activeSbPanel = 'elements'"
                    >Page Elements</tabs-item
                  >
                  <tabs-item
                    :active="activeSbPanel === 'settings'"
                    @click="activeSbPanel = 'settings'"
                    >Page Settings</tabs-item
                  >
                </tabs>
              </slot>

              <div
                class="c-pb__sb-elements"
                v-show="activeSbPanel === 'elements'"
              >
                <draggable
                  v-model="elementList"
                  :move="onElementMove"
                  :options="{
                    sort: false,
                    group: { name: 'pageElements', pull: 'clone', put: false },
                  }"
                  @start="onListElementDragStart"
                  @end="onListElementDragEnd"
                >
                  <!-- <div v-for="element in myArray" :key="element.id">{{element.name}}</div> -->
                  <div
                    v-for="(element, key) in elementList"
                    :key="key"
                    :data-type="element.type"
                  >
                    <div
                      class="c-pb__element"
                      @click="setCurrentElement(element, false)"
                    >
                      <div class="c-pb__element-icon">
                        <icon
                          width="42"
                          height="42"
                          v-if="element.icon"
                          :name="element.icon"
                          :fill="element.iconFill"
                        ></icon>
                      </div>
                      <div class="c-pb__element-content">
                        <span class="c-pb__element-title">{{
                          element.title
                        }}</span>
                        <span class="c-pb__element-description">{{
                          element.description
                        }}</span>
                      </div>
                    </div>
                  </div>
                </draggable>
              </div>
              <slot name="sidebars">
                <div
                  class="c-pb__sb-settings"
                  v-show="activeSbPanel === 'settings'"
                >
                  <form-field
                    full-width
                    label="Page Slug"
                    name="page_slug"
                    type="text"
                    v-model="currentPage.slug"
                  ></form-field>

                  <form-field
                    v-if="parentPages"
                    label="Parent Page"
                    name="parent_page"
                    helper-text="Choose where your page sits within the navigation"
                  >
                    <form-type-ahead-select
                      v-model="parentPage"
                      id="parent_page"
                      name="parent_page"
                      v-validate="{ required: true }"
                      :options="parentPages"
                      async-action="pages/getParentPagesForSelect"
                      display-field="title"
                      @input="onParentPageInput"
                    />
                  </form-field>
                  <loading v-else></loading>

                  <!-- Are we a child page/Do we have children -->
                  <form-field
                    v-if="currentChildren && currentChildren.length > 0"
                    label="Update Child Menu order?"
                    name="child_menu"
                    helper-text="You can rearrange the order child pages appear here."
                  >
                    <form-toggle v-model="reorderMenu" class="mb-12" />
                    <sortable-list
                      v-model="currentChildren"
                      :enabled="reorderMenu"
                      v-if="reorderMenu"
                    >
                      <sortable-item
                        v-for="child in currentChildren"
                        :key="child.id"
                        @moveUp="moveItemUp(child)"
                        @moveDown="moveItemDown(child)"
                        :enabled="reorderMenu"
                        >{{ child.title }}</sortable-item
                      >
                    </sortable-list>
                  </form-field>

                  <form-field
                    label="Review Date"
                    name="review_date"
                    helper-text="Defaults to 1 year unless overwritten."
                  >
                    <form-datepicker
                      small
                      v-model="pageSettings.reviewDate"
                      name="review_date"
                      format="DD / MM / YYYY"
                      @input="onReviewDateInput"
                      input-class="c-form__input"
                    ></form-datepicker>
                  </form-field>

                  <form-field
                    label="Interest Groups"
                    name="interest_groups"
                    helper-text="Help users find your event by tagging related interest groups:"
                  >
                    <form-multiselect
                      :show-valid-tick="true"
                      v-model="interestgroups"
                      :options="interestGroups"
                      :multiple="true"
                      label="name"
                      track-by="name"
                      @input="onInterestGroupUpdate"
                      :taggable="true"
                    ></form-multiselect>
                  </form-field>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL (CREATE/EDIT PROPS/CONTENT) -->
    <portal to="modal">
      <modal
        show-close-button
        v-if="propsModalOpen"
        @close="propsModalOpen = false"
        :outsideClose="!sidebarShouldStick"
      >
        <template slot="header">
          <h3 v-if="editingElement">Edit {{ currentElement.title }}</h3>
          <h3 v-else>Add {{ currentElement.title }}</h3>
        </template>
        <pb-element-form
          :editMode="editingElement"
          :element="currentElement"
          @update="onElementFormUpdate"
          @create="onElementFormCreate"
          @remove="removeElement"
          @updateProp="onElementFormPropUpdate"
          :type="type"
        ></pb-element-form>
      </modal>
    </portal>

    <span class="js-add-elem" :class="{ 'js-add-elem--isOpen': mobileToggle }">
      <base-button :primary="true" @click="toggleSidebar">+</base-button>
    </span>
  </div>

  <loading v-else></loading>
</template>

<script>
import draggable from "vuedraggable";
import Sticky from "vue-sticky-directive";

import PbElement from "@/components/PbElement";
import PbElementBar from "@/components/PbElementBar";
import PbElementForm from "@/components/PbElementForm";
import { FormToggle } from "@/components/forms";
import SortableList from "@/components/SortableList";
import SortableItem from "@/components/SortableItem";
import { mapState } from "vuex";
const FormTypeAheadSelect = () =>
  import("@/components/FormTypeAheadSelect.vue");
export default {
  name: "page-builder",

  props: {
    pageId: Number,
    reviewDate: [Date, String],
    interestgroups: [Object, Array],
    showBanner: Boolean,
    activeSbPanel: {
      type: String,
      default: "elements",
    },
    type: {
      type: String,
      default: "document",
    },
    loadPages: {
      type: Boolean,
      default: true,
    },
    filesEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
      drag: false,
      pageElementDrag: false,
      listElementDrag: false,
      parentPages: [],
      filterFormInputs: false,

      pageSettings: {
        reviewDate: null,
      },
      windowInnerHeight: 0,
      pageBuilderHeight: 0,
      mobileToggle: false,
      reorderMenu: false,
      origChildrenState: null
    };
  },

  created() {
    if (this.loadPages) {
      this.$store.dispatch("pages/getParentPagesForSelect").then((resp) => {
        this.parentPages = resp;
      });
    }
    this.$store.dispatch("InterestGroups/list", { flat: true });
    this.origChildrenState = {...this.currentChildren}
    this.reorderMenu = this.currentChildren.length > 1
  },

  mounted() {
    this.windowInnerHeight = `${window.innerHeight - 100}px`;

    //need to reset page filter
    this.$store.commit("pages/setSearchQuery", "");
    this.$store.commit("pages/setTabFilter", "approved");
    this.$store.commit("pages/setPagination", false);

    this.$nextTick(() => {
      this.pageBuilderHeight = this.$refs.pbContainer
        ? this.$refs.pbContainer.clientHeight
        : 800;

      window.addEventListener("resize", () => {
        this.windowInnerHeight = `${window.innerHeight - 100}px`;
      });
    });
  },

  updated() {
    this.pageBuilderHeight = this.$refs.pbContainer
      ? this.$refs.pbContainer.clientHeight
      : 800;
  },

  computed: {
    ...mapState({
      interestGroups: (state) => state.InterestGroups.collection,
    }),
    currentChildren: {
      get() {
        return this.$store.state.pages.currentChildren;
      },
      set(newVal, oldVal) {
        this.$store.commit("pages/SET_CHILDREN", newVal);
      },
    },
    sidebarShouldStick() {
      return (
        this.pageBuilderHeight > this.sidebarHeight && window.outerWidth >= 992
      );
    },
    sidebarHeight() {
      if (document.querySelector(".sidebar-holder")) {
        return document.querySelector(".sidebar-holder").clientHeight;
      }

      return 100;
    },
    propsModalOpen: {
      get() {
        return this.$store.state.pageElements.propsModalOpen;
      },
      set(newVal) {
        this.$store.commit("pageElements/setModalOpen", newVal);
      },
    },

    editingElement: {
      get() {
        return this.$store.state.pageElements.editMode;
      },
      set(newVal) {
        this.$store.commit("pageElements/setEditMode", newVal);
      },
    },

    sidebarWrapperStyleObject() {
      if (this.sidebarShouldStick) {
        return {
          position: "absolute",
          top: 0,
          right: "15px",
          left: 0,
          bottom: "200px",
        };
      }
    },

    sidebarStyleObject() {
      return {
        maxHeight: document.querySelector(".c-section-block__main")
          ? document.querySelector(".c-section-block__main").clientHeight
          : 800,
      };
    },

    parentPage: {
      get() {
        if (!this.currentPage.parent_data) {
          return {};
        }
        const returnObj = {
          id: this.currentPage.parent_id,
          title: this.currentPage.parent_data.title,
        };

        return returnObj;
      },
      set(value) {
        this.$store.commit("pages/setParentPage", value);
      },
    },

    currentPage: {
      get() {
        return this.$store.state.pages.currentPage;
      },
      set(value) {
        this.$store.commit("pages/setCurrentPage", value);
      },
    },

    elementList() {
      if (["survey", "form"].includes(this.type)) {
        if (!this.filterFormInputs) {
          return this.$store.getters["pageElements/elementListForm"].filter(
            (elem) => !["media-file", "media-file-upload"].includes(elem.type)
          );
        }
        return this.$store.getters["pageElements/elementListForm"];
      }
      return this.$store.getters["pageElements/elementList"];
    },

    pageElements: {
      get() {
        return this.$store.state.pageElements.pageElements;
      },
      set(elements) {
        this.$store.commit("pageElements/setPageElements", elements);
      },
    },

    elementsForRender() {
      return this.$store.getters["pageElements/forRender"];
    },

    elementsForEdit() {
      return this.$store.getters["pageElements/forEdit"];
    },

    currentElement: {
      get() {
        return this.$store.state.pageElements.currentElement;
      },
      set(element) {
        this.$store.commit("pageElements/setCurrentElement", element);
        //close mobile menu
        this.mobileToggle = false;
      },
    },

    currentElementIndex: {
      get() {
        return this.$store.state.pageElements.currentElementIndex;
      },
      set(index) {
        this.$store.commit("pageElements/setCurrentElementIndex", index);
      },
    },
  },

  watch: {
    reorderMenu: {
      immediate: true,
      handler(newVal) {
        this.$emit('resetChildren', newVal)
      }
    },
    reviewDate: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        this.pageSettings.reviewDate = newValue;
      },
    },
    filesEnabled: {
      immediate: true,
      handler(newVal) {
        this.filterFormInputs = newVal;
      },
    },
  },

  methods: {
    // move an item up the list
    moveItemUp(item) {
      const id = item.id;
      let from = this.currentChildren.findIndex((item) => item.id === id);
      let newArray = this.currentChildren.slice();
      if (from > 0) {
        const to = from - 1;
        newArray.splice(from, 0, ...newArray.splice(to, 1));
        this.$store.commit("pages/SET_CHILDREN", newArray);
      }
    },

    // move an item down the list
    moveItemDown(item) {
      const id = item.id;
      let from = this.currentChildren.findIndex((item) => item.id === id);
      let newArray = this.currentChildren.slice();

      if (from < this.currentChildren.length - 1) {
        const to = from + 1;
        newArray.splice(from, 0, ...newArray.splice(to, 1));
        this.$store.commit("pages/SET_CHILDREN", newArray);
      }
    },
    onParentPageInput(value) {
      if (value && value.id) {
        this.currentPage.parent_id = value.id;
        return;
      }
      this.currentPage.parent_id = null;
    },

    toggleSidebar() {
      this.mobileToggle = !this.mobileToggle;
    },

    onInterestGroupUpdate(val) {
      this.$emit("interestGroupUpdated", val);
    },

    onReviewDateInput(value) {
      const dateFormatted = this.$dayjs(value).format("YYYY-MM-DD HH:mm:ss");
      this.$emit("reviewDateUpdated", dateFormatted);
    },

    onPageElementDragStart(event) {
      this.drag = true;
      this.pageElementDrag = true;
    },

    onPageElementDragEnd(event) {
      this.drag = false;
      this.pageElementDrag = false;
    },

    onListElementDragStart(event) {
      this.drag = true;
      this.listElementDrag = true;
    },

    onListElementDragEnd(event) {
      this.drag = false;
      this.listElementDrag = false;

      this.editElement(event.newIndex);
    },

    onElementMove(event, originalEvent) {
      //
    },

    editElement(index) {
      if (!this.elementsForEdit[index].hideModalWindow) {
        this.setCurrentElement(this.elementsForEdit[index], true);
        this.currentElementIndex = index;
      }
    },

    setCurrentElement(element, editMode = true) {
      if (!element.hideModalWindow) {
        this.currentElement = {};

        this.currentElement = JSON.parse(JSON.stringify(element));

        this.editingElement = !!editMode;

        // if there are no properties, just add in
        this.propsModalOpen = true;
      } else {
        this.onElementFormCreate(element);
      }
    },

    onElementFormUpdate(element) {
      this.$store.commit("pageElements/update", element);
      this.propsModalOpen = false;
    },

    onElementFormCreate(element) {
      this.$store.dispatch("pageElements/add", element);
      this.propsModalOpen = false;
    },

    removeElement(elIndex) {
      this.$store.dispatch("createDialog", {
        confirm: () => {
          this.$store.dispatch("pageElements/remove", elIndex);
        },
      });
    },

    duplicateElement(elIndex) {
      this.$store.dispatch("pageElements/duplicate", elIndex);
    },

    onElementFormPropUpdate(element, key, value) {
      this.$store.dispatch("pageElements/updateProp", { key, value });
    },
  },

  components: {
    PbElement,
    PbElementBar,
    PbElementForm,
    draggable,
    FormTypeAheadSelect,
    SortableList,
    SortableItem,
    FormToggle,
  },

  directives: { Sticky },
};
</script>

<style lang="scss">
.c-pb {
  margin: -1em 0;
}
.c-pb__container {
  padding: 1em 0;
  position: relative;
  min-height: 800px;
}

.c-pb__no-elements {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  margin-top: 2em;

  display: flex;
  flex-direction: column;
}
.c-pb__no-elements-text {
  display: block;
  font-family: $font-frutiger-light;
  margin-top: 0.5em;
  font-size: 24px;
  color: #202020;
  text-align: center;
}
.c-pb__no-elements .c-inline-svg {
  flex-grow: 1;

  display: flex;
  margin-top:5rem;
  align-items: start;
  justify-content: center;
  svg {
    max-width: 400px;
  }
}

.c-pb__drag-zone {
  position: relative;
  z-index: 20;
  min-height: 600px;
}

.c-pb__row {
  background: $colour-nhs-white;
  border: 2px solid $colour-nhs-pale-grey;
  padding: 0.5em;
  margin-bottom: 1em;
  position: relative;
  transition: transform 0.3s ease;
  border-radius: 4px;

  min-height: 56px;

  &:hover {
    border-color: $colour-nhs-dark-grey;
  }

  &:hover .c-pb-element-bar {
    display: flex;
  }
}

.c-pb__sidebar {
  border-left: 1px solid $colour-nhs-pale-grey;
  height: auto;
  padding-top: 1em;
  padding-left: 1em;
  padding-bottom: 1em;
}

.vue-sticky-el {
  top: 0;
}

// Element List
.c-pb__element {
  background: $colour-nhs-white;
  border-bottom: 1px solid $colour-nhs-pale-grey;
  padding: 1em;
  cursor: move;
  transition: all 0.3s ease;

  display: flex;
  justify-content: flex-start;
  align-content: flex-start;
  align-items: stretch;

  &-icon {
    min-width: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #c9ced4;
  }

  &-content {
    flex-grow: 1;
    width: auto;
    padding-left: 0.5em;
  }

  &-title,
  &-description {
    display: block;
    font-size: 14px;
  }

  &-title {
    font-size: 16px;
    margin-bottom: 0.5em;
  }

  &:hover {
    position: relative;
    border-color: transparent;
    z-index: 10;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.11);
  }
  &:hover &-icon {
    color: $colour-nhs-blue;
  }

  &:hover {
    //not sure why rect + path behaves differently
    rect {
      stroke: $colour-nhs-blue;
      + path {
        fill: $colour-nhs-blue;
        stroke: $colour-nhs-blue;
      }
    }
  }
}

.c-pb__sidebar.top-sticky {
  margin-top: 140px;
  height: auto;
}

.sortable-chosen {
  transform: scale(0.96);
}

.sortable-drag {
  opacity: 1;
}

.sortable-ghost {
  opacity: 0.9;
}

.sidebar-holder {
  position: relative;
  height: 100%;
  overflow-x: hidden;
}

.top-sticky {
  max-height: 100vh;
  overflow: scroll;
}

.js-sidebar {
  transition: all ease-in-out 0.2s;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  transform: translateX(100vw);
  position: fixed;
  top: 50px;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 40;
  background: white;

  @media screen and (min-width: 992px) {
    display: block;
    transform: none;
    position: relative;
    top: auto;
    z-index: 10;
  }

  &--isOpen {
    transform: translateX(0);
  }
}

.js-add-elem {
  position: fixed;
  bottom: 60px;
  right: 10px;
  z-index: 50;

  @media screen and (min-width: 992px) {
    display: none;
  }

  button {
    cursor: pointer;
    transition: all ease-in-out 0.2s;
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
    transform-origin: 50% 50%;
    font-size: 2em !important;
    padding: 6px 16px 12px !important; // should this really be that complicated!?
    line-height: 1;
    // padding: calc(0.875em 1.0625em !important;
  }

  &--isOpen {
    button {
      transform: rotate(225deg);
    }
  }
}
</style>
